<div class="position-dialog">
  <h2 mat-dialog-title>{{eventTitle}}</h2>
  <mat-dialog-content>
    <form [formGroup]="eventForm">
      <!-- Name -->
      <div class="form-row">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Name</mat-label>
          <input matInput formControlName="name" maxlength="50" (input)="onNameInput()"
            [readonly]="eventForm.get('name')?.disabled" />
          @if (showCharCount) {
          <mat-hint align="end">{{ characterCount }}/50 characters</mat-hint>
          }
          @if (eventForm.get('name')?.hasError('required')) {
          <mat-error>Name is required</mat-error>
          }
          @if (eventForm.get('name')?.hasError('maxlength')) {
          <mat-error>Maximum 50 characters allowed</mat-error>
          }
        </mat-form-field>
      </div>
      <!-- Rich Text Editor -->
      <div class="form-row">
        <div [ngClass]="{
          'editor-wrapper': true,
          'editor-invalid': eventForm.get('content')?.invalid && eventForm.get('content')?.touched
        }">
          <ngx-editor-menu [editor]="editor" [toolbar]="toolbar"></ngx-editor-menu>
          <ngx-editor [editor]="editor" formControlName="content" placeholder="Add Description"></ngx-editor>
        </div>
        @if (eventForm.get('content')?.hasError('required') && eventForm.get('content')?.touched) {
        <div class="field-error">Description is required</div>
        }
      </div>
      <!-- Dates and Times -->
      <div class="form-row date-time-row">
        <mat-form-field appearance="outline">
          <mat-label>Start Date</mat-label>
          <input matInput [matDatepicker]="startPicker" formControlName="startDate" [min]="minStartDate" />
          <mat-datepicker-toggle matSuffix [for]="startPicker"></mat-datepicker-toggle>
          <mat-datepicker #startPicker></mat-datepicker>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Start Time</mat-label>
          <input matInput type="time" formControlName="startTime" #startTimeInput />
          <button mat-icon-button matSuffix type="button" aria-label="Open start time picker"
            (click)="openTimePicker(startTimeInput)">
            <mat-icon>schedule</mat-icon>
          </button>
          @if (eventForm.get('startTime')?.hasError('pastTime')) {
          <mat-error>Start time (ET) cannot be in the past</mat-error>
          }
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>End Date</mat-label>
          <input matInput [matDatepicker]="endPicker" formControlName="endDate"
            [min]="eventForm.get('startDate')?.value || minStartDate" />
          <mat-datepicker-toggle matSuffix [for]="endPicker"></mat-datepicker-toggle>
          <mat-datepicker #endPicker></mat-datepicker>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>End Time</mat-label>
          <input matInput type="time" formControlName="endTime" #endTimeInput />
          <button mat-icon-button matSuffix type="button" aria-label="Open end time picker"
            (click)="openTimePicker(endTimeInput)">
            <mat-icon>schedule</mat-icon>
          </button>
          @if (eventForm.get('endTime')?.hasError('beforeStart')) {
          <mat-error>End time must be after start time</mat-error>
          }
        </mat-form-field>
      </div>
      <!-- Type / Capacity / Room / Image -->
      <div class="form-row fields-row">
        <mat-form-field appearance="outline">
          <mat-label>Event Types</mat-label>
          <mat-select formControlName="type" multiple (selectionChange)="onTypeChange($event)">
            @for (tag of eventTags; track tag) {
            <mat-option [value]="tag.imageId">{{ tag.name }}</mat-option>
            }
          </mat-select>
          @if (eventForm.get('type')?.hasError('required')) {
          <mat-error>Please select at least one event type</mat-error>
          }
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Capacity</mat-label>
          <input matInput type="number" min="1" formControlName="capacity" />
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Room</mat-label>
          <input matInput formControlName="room" />
        </mat-form-field>
        <mat-form-field appearance="outline" class="image-picker-field" (click)="openImagePicker()">
          <mat-label>Event Image</mat-label>
          <input matInput formControlName="image" readonly [value]="getSelectedImageName()"
            placeholder="Click to select an image" class="image-picker-input" />
        </mat-form-field>
      </div>
      <!-- Image Thumbnail -->
      @if (eventForm.get('image')?.value) {
      <div class="image-preview-inline">
        <img [src]="getSelectedImageUrl()" [alt]="getSelectedImageName()" class="inline-thumbnail"
          (click)="expandSelectedImage()" title="Click to view full image" />
        @if (isDefaultImage()) {
        <div class="image-hint">
          <mat-icon>info</mat-icon>
          <span>Default image for {{ getPrimaryEventType() }} events</span>
        </div>
        }
      </div>
      }
    </form>
  </mat-dialog-content>
  <mat-dialog-actions align="end">
    <button mat-button (click)="onCancel()">Cancel</button>
    <button mat-raised-button color="primary" (click)="onSubmit()" [disabled]="eventForm.invalid">Save</button>
  </mat-dialog-actions>
</div>
<!-- Full Image Modal -->
@if (selectedImageForExpand) {
<div class="image-modal" (click)="closeExpandedImage()">
  <button class="close-btn" (click)="closeExpandedImage()">×</button>
  <img [src]="selectedImageForExpand.content" [alt]="selectedImageForExpand.name" class="expanded-image"
    (click)="$event.stopPropagation()" />
</div>
}
<!-- Image Picker Modal -->
@if (showImagePicker) {
<div class="image-picker-modal">
  <div class="picker-content" (click)="$event.stopPropagation()">
    <div class="picker-header">
      <h3>Select an Image</h3>
      <button mat-icon-button class="close-modal-btn" (click)="closeImagePicker()" title="Close">
        <mat-icon>close</mat-icon>
      </button>
    </div>
    <div class="rv-tabs">
      <mat-button-toggle-group class="rv-tabs__group mat-elevation-z0" color="primary" name="cats"
        aria-label="Image categories" [value]="selectedCategory" (change)="selectCategory($event.value)">
        @for (cat of categories; track cat) {
        <mat-button-toggle [value]="cat">{{ cat | titlecase }}</mat-button-toggle>
        }
      </mat-button-toggle-group>
    </div>
    <div class="picker-grid">
      @for (img of filteredImages; track trackByImg($index, img)) {
      <div class="picker-image-item" [class.selected]="isImageSelected(img.key)"
        (click)="selectImageFromPicker(img.key)">
        <img [src]="img.content" [alt]="img.name" />
        @if (isImageSelected(img.key)) {
        <div class="selected-check">
          <mat-icon>check_circle</mat-icon>
        </div>
        }
        <div class="picker-image-info">
          <span class="image-name">{{ img.key }}</span>
        </div>
      </div>
      }
    </div>
    <div class="picker-footer">
      <span class="image-count">
        <mat-icon>image</mat-icon>
        {{ filteredImages.length }} {{ filteredImages.length | i18nPlural: { '=0': 'image', '=1': 'image', 'other':
        'images' } }} available
      </span>
      <div class="picker-actions">
        <button mat-stroked-button (click)="closeImagePicker()">
          <mat-icon>close</mat-icon> Cancel
        </button>
        <button mat-raised-button color="primary" (click)="confirmImageSelection()">
          <mat-icon>check</mat-icon> Apply Selection
        </button>
      </div>
    </div>
  </div>
</div>
}